import * as PI from '@sosocio/creator-types/project';
import * as svgUtils from '@sosocio/frontend-utils/svg';
import * as urlTools from '@sosocio/frontend-utils/url';
import loadImage from 'services/load-image';
import { ConfigModule } from 'store';
import sanitizeSVG from 'utils/sanitize-svg';

interface RasterLogoVectorData {
	content: string;
	colors: PI.PageObjectVectorColors;
}

type RasterLogoBackgroundMaskObjectModel = Pick<PI.PageObjectModel, '_vectorColors' | '_vectorSVG' | 'colorReplacement' | 'ext' | 'rasterBackgroundRemoved'>;

interface ApplyRasterLogoBackgroundMaskOptions {
	image: HTMLImageElement;
	objectModel: RasterLogoBackgroundMaskObjectModel;
	photoModel?: PI.PhotoModel;
}

function isRasterLogoWithVectorSource(
	objectModel: RasterLogoBackgroundMaskObjectModel,
	photoModel?: PI.PhotoModel,
): photoModel is PI.PhotoModel & { url: string } {
	if (
		objectModel.ext === 'svg'
		|| !photoModel?.url
		|| photoModel.type !== 'logo'
	) {
		return false;
	}

	return urlTools.parse(photoModel.url).extension?.toLowerCase() === 'svg';
}

function getTransparentBackgroundReplacement(
	objectModel: RasterLogoBackgroundMaskObjectModel,
	vectorColors: PI.PageObjectVectorColors,
): PI.PageObjectColorReplacementModel | undefined {
	const background = vectorColors.background;

	if (
		!background
		|| background.color === 'transparent'
	) {
		return undefined;
	}

	const backgroundColors = new Set([
		background.color,
		...background.matches,
	]);

	return objectModel.colorReplacement?.find((replacement) => (
		backgroundColors.has(replacement.color)
		&& replacement.replace.real.toLowerCase() === 'transparent'
	));
}

/**
 * Sanitizes cached or freshly loaded raster-logo vector data before analysis.
 *
 * @param objectModel {@link RasterLogoBackgroundMaskObjectModel} containing optional cached SVG data.
 * @param photoModel {@link PI.PhotoModel} providing the remote SVG source.
 * @returns {@link RasterLogoVectorData} with markup and colors derived from it.
 */
async function getRasterLogoVectorData(
	objectModel: RasterLogoBackgroundMaskObjectModel,
	photoModel: PI.PhotoModel & { url: string },
): Promise<RasterLogoVectorData> {
	const svgContent = objectModel._vectorSVG
		?? (await svgUtils.load(photoModel.url)).content;
	const sanitizedSVG = sanitizeSVG(svgContent);
	const svgColors = await svgUtils.getColors(
		sanitizedSVG,
		ConfigModule['logoColors.mergeSimilarThreshold'],
	);

	return {
		content: sanitizedSVG,
		colors: svgColors,
	};
}

async function loadPNGDataURL(dataUrl: string): Promise<HTMLImageElement> {
	const { image } = await loadImage(
		dataUrl,
		{
			crossOrigin: null,
			outputFormat: 'png',
		},
	);

	return image;
}

async function applyMaskToImage(
	image: HTMLImageElement,
	maskDataUrl: string,
): Promise<HTMLImageElement> {
	const maskImage = await loadPNGDataURL(maskDataUrl);
	const canvasElement = document.createElement('canvas');
	const imageWidth = Math.max(
		1,
		Math.round(image.naturalWidth || image.width),
	);
	const imageHeight = Math.max(
		1,
		Math.round(image.naturalHeight || image.height),
	);
	canvasElement.width = imageWidth;
	canvasElement.height = imageHeight;
	const canvasContext = canvasElement.getContext('2d');

	if (!canvasContext) {
		throw new Error('Failed to get canvas 2D context');
	}

	canvasContext.drawImage(
		image,
		0,
		0,
		imageWidth,
		imageHeight,
	);
	canvasContext.globalCompositeOperation = 'destination-in';
	canvasContext.drawImage(
		maskImage,
		0,
		0,
		imageWidth,
		imageHeight,
	);

	return loadPNGDataURL(canvasElement.toDataURL('image/png'));
}

export async function applyRasterLogoBackgroundMask({
	image,
	objectModel,
	photoModel,
}: ApplyRasterLogoBackgroundMaskOptions): Promise<HTMLImageElement> {
	if (!isRasterLogoWithVectorSource(
		objectModel,
		photoModel,
	)) {
		return image;
	}

	if (!objectModel.rasterBackgroundRemoved) {
		return image;
	}

	const vectorData = await getRasterLogoVectorData(
		objectModel,
		photoModel,
	);
	const backgroundReplacement = getTransparentBackgroundReplacement(
		objectModel,
		vectorData.colors,
	);

	if (
		!backgroundReplacement
		|| !vectorData.colors.background
	) {
		return image;
	}

	const mask = await svgUtils.createMaskFromSVGColorRemoval(
		vectorData.content,
		vectorData.colors,
		vectorData.colors.background.color,
	);

	if (!mask) {
		return image;
	}

	return applyMaskToImage(
		image,
		mask.dataUrl,
	);
}
